/**
 * Welcome card shown when the widget first opens.
 *
 * The visitor lands on a calm welcome surface, NOT directly in the
 * chat composer. The card introduces the agent, exposes "Start
 * gesprek" as the primary affordance, and lists "Direct contact"
 * pills (WhatsApp, email, phone) for the channels the tenant has
 * configured. Channels that are unavailable (no tenant config, or
 * WhatsApp pre-launch) are filtered out entirely; we no longer
 * render a "Binnenkort beschikbaar" placeholder because it made the
 * card feel half-finished. When every channel is unavailable the
 * entire section is omitted and the welcome ends after the chat
 * card.
 *
 * Reference: the Watermelon widget design the founder shared as the
 * quality bar. We diverge only where their copy is louder than the
 * RespondHero brand voice (no exclamation marks, sentence case, no
 * em-dashes per CLAUDE.md sectie 11).
 *
 * Authoring rule: this component owns layout + interaction, NOT data
 * fetching. All tenant values arrive via props from `app.tsx` so the
 * welcome card is fully testable with a static config object.
 */

import type { JSX } from "preact";
import { useEffect, useMemo, useRef } from "preact/hooks";
import { type ChannelPillSpec, buildChannelPills } from "./channels";
import { format, type tFor } from "./i18n";
// `tFor` is imported as a value just so we can capture `ReturnType<typeof
// tFor>` below; the value itself is never called from this module.
import { ChevronRightIcon, CloseIcon, MailIcon, PhoneIcon, WhatsAppIcon } from "./icons";
import type { WidgetTenantConfig } from "./types";

interface WelcomeProps {
  tenant: WidgetTenantConfig;
  t: ReturnType<typeof tFor>;
  brandLogoUrl: string | null;
  avatarUrl: string | null;
  avatarLetter: string;
  /** Visitor taps "Start gesprek". Parent transitions to the chat state. */
  onStartConversation: () => void;
  /** × button. Parent fully closes the widget back to the launcher. */
  onClose: () => void;
}

/**
 * Render the welcome surface. Exported so the dashboard preview can
 * mount the same component server-side, guaranteeing the operator
 * sees what the visitor will see (no drift between preview and
 * production).
 */
export function WelcomeCard({
  tenant,
  t,
  brandLogoUrl,
  avatarUrl,
  avatarLetter,
  onStartConversation,
  onClose,
}: WelcomeProps): JSX.Element {
  const brandLabel = tenant.brand_name || tenant.tenant_name;
  const welcomeLine = format(t.welcome_card_title, { brand: brandLabel });
  const channels = tenant.channels;
  // Pre-compute the active pill set so we can decide whether to
  // render the "Direct contact" section at all. Unavailable channels
  // are filtered out rather than shown as placeholders; if nothing
  // is configured the section disappears entirely.
  const activeChannelPills = useMemo(
    () =>
      buildChannelPills(channels, {
        whatsapp: t.welcome_channel_whatsapp_label,
        email: t.welcome_channel_email_label,
        phone: t.welcome_channel_phone_label,
        unavailable: t.welcome_channel_unavailable,
      }).filter((pill): pill is ChannelPillSpec & { href: string } => pill.href !== null),
    [channels, t],
  );

  // Autofocus the primary CTA on first paint. The widget panel is
  // already shown as a `role="dialog"`; mounting the welcome state is
  // the moment the visitor enters that dialog, so focus should land on
  // the high-affordance action (Start gesprek) rather than the close
  // button. A full focus trap is a larger refactor that would also
  // affect the chat-state and voice-modal dialogs; deferred (TODO
  // below) so this PR stays scoped.
  // TODO(a11y): introduce a shared focus-trap utility for all three
  // dialog surfaces (welcome card, chat panel, voice modal).
  const ctaRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    ctaRef.current?.focus();
  }, []);

  return (
    <div
      class="rh-welcome"
      // biome-ignore lint/a11y/useSemanticElements: matches the pre-existing
      // `<div role="dialog">` pattern in app.tsx + VoiceModal.tsx; switching
      // here would require migrating those two sites too plus open-state
      // management. Out of scope for this PR.
      role="dialog"
      aria-modal="true"
      aria-label={brandLabel}
    >
      <div class="rh-welcome-header">
        <div class="rh-welcome-header-brand">
          {brandLogoUrl ? (
            <img class="rh-welcome-header-mark" src={brandLogoUrl} alt="" aria-hidden="true" />
          ) : (
            <div class="rh-welcome-header-mark-fallback" aria-hidden="true">
              {brandLabel[0]?.toUpperCase() ?? "R"}
            </div>
          )}
          <span class="rh-welcome-header-name">{brandLabel}</span>
        </div>
        <button type="button" class="rh-icon-btn" aria-label="Close" onClick={onClose}>
          <CloseIcon />
        </button>
      </div>

      <div class="rh-welcome-body">
        {/* Card 1: warm welcome line + powered-by attribution. */}
        <section class="rh-welcome-greeting">
          <h2 class="rh-welcome-greeting-line">{welcomeLine}</h2>
          <p class="rh-welcome-powered-by">
            <span class="rh-welcome-coral-dot" aria-hidden="true" />
            {t.welcome_powered_by}
          </p>
        </section>

        {/* Card 2: chat with {agent}. Coral CTA. */}
        <section class="rh-welcome-chat-card">
          <div class="rh-welcome-chat-card-row">
            <div class="rh-welcome-chat-avatar">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" aria-hidden="true" />
              ) : (
                <span>{avatarLetter}</span>
              )}
            </div>
            <div class="rh-welcome-chat-text">
              <p class="rh-welcome-chat-title">
                {format(t.welcome_chat_card_title, { name: tenant.chatbot_name })}
              </p>
              <p class="rh-welcome-chat-intro">{t.welcome_chat_card_intro}</p>
            </div>
          </div>
          <button ref={ctaRef} type="button" class="rh-welcome-cta" onClick={onStartConversation}>
            {t.welcome_chat_card_cta}
          </button>
        </section>

        {/* Card 3: direct-contact pills. Only rendered when at least
            one channel is actually available; otherwise the welcome
            card ends after the chat card so we never expose a row of
            placeholder pills. */}
        {activeChannelPills.length > 0 ? (
          <section class="rh-welcome-channels">
            <h3 class="rh-welcome-channels-heading">{t.welcome_direct_contact_heading}</h3>
            <ChannelPills pills={activeChannelPills} />
          </section>
        ) : null}
      </div>
    </div>
  );
}

interface ChannelPillsProps {
  /** Pre-filtered list of pills that are actually clickable. */
  pills: ReadonlyArray<ChannelPillSpec & { href: string }>;
}

const CHANNEL_ICONS: Record<ChannelPillSpec["key"], () => preact.JSX.Element> = {
  whatsapp: () => <WhatsAppIcon />,
  email: () => <MailIcon />,
  phone: () => <PhoneIcon />,
};

/**
 * Vertically stacked anchor pills. Order is preserved from
 * `buildChannelPills` (WhatsApp, email, phone); unavailable channels
 * are removed upstream so every pill rendered here is interactive.
 */
function ChannelPills({ pills }: ChannelPillsProps): JSX.Element {
  return (
    <ul class="rh-welcome-channel-list">
      {pills.map((pill) => (
        <ChannelPill
          key={pill.key}
          icon={CHANNEL_ICONS[pill.key]()}
          label={pill.label}
          href={pill.href}
          // Sublabel is the trust-signal value (actual email address
          // or phone formatting). Active pills always have one set by
          // `buildChannelPills`; we still tolerate a missing value
          // defensively rather than asserting.
          sublabel={pill.sublabel ?? ""}
        />
      ))}
    </ul>
  );
}

interface ChannelPillProps {
  icon: preact.JSX.Element;
  label: string;
  href: string;
  sublabel: string;
}

function ChannelPill({ icon, label, href, sublabel }: ChannelPillProps): JSX.Element {
  return (
    <li class="rh-welcome-channel">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        class="rh-welcome-channel-link"
        aria-label={sublabel ? `${label}, ${sublabel}` : label}
      >
        <span class="rh-welcome-channel-icon" aria-hidden="true">
          {icon}
        </span>
        <span class="rh-welcome-channel-body">
          <span class="rh-welcome-channel-label">{label}</span>
          {sublabel ? <span class="rh-welcome-channel-sub">{sublabel}</span> : null}
        </span>
        <span class="rh-welcome-channel-chevron" aria-hidden="true">
          <ChevronRightIcon />
        </span>
      </a>
    </li>
  );
}
